import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Field, TextInput, TextArea, SelectInput } from "@/components/site/Form";
import { Notice } from "@/components/site/Bits";
import { useServiceArea, ServiceAreaNotice } from "@/components/site/ServiceArea";
import { Phone, MessageCircle, CalendarCheck, ScanSearch, FileUp, Calculator, Building2 } from "lucide-react";

export const BUSINESS = {
  phone: "0333 335 6152",
  phoneHref: "tel:+443333356152",
  email: "info@procuregroup.uk",
  whatsapp: "https://wa.me/443333356152",
};

export const track = (slug, event) => {
  if (!slug) return;
  api
    .post("/location-event", { slug, event, landing_page: window.location.pathname })
    .catch(() => {});
};

const ACTIONS = [
  { key: "booking", to: "/book-an-inspection", label: "Book a Damp Survey", icon: CalendarCheck, primary: true },
  { key: "damp_checker", to: "/tools/check-my-damp", label: "Check Your Damp Using AI", icon: ScanSearch },
  { key: "report_review", to: "/tools/upload-my-survey", label: "Upload an Existing Report", icon: FileUp },
  { key: "estimate", to: "/tools/get-an-estimate", label: "Request a Written Estimate", icon: Calculator },
];

export const LocalActions = ({ slug, compact = false }) => (
  <div className="flex flex-wrap gap-3" data-testid="local-actions">
    {ACTIONS.slice(0, compact ? 2 : 4).map(({ key, to, label, icon: Icon, primary }) => (
      <Link
        key={key}
        to={to}
        onClick={() => track(slug, key)}
        data-testid={`local-action-${key}`}
        className={`inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold transition-colors ${
          primary
            ? "bg-pg-primary text-white hover:bg-pg-dark"
            : "border border-pg-border bg-white text-pg-dark hover:border-pg-primary"
        }`}
      >
        <Icon className="h-4 w-4" /> {label}
      </Link>
    ))}
    <a
      href={BUSINESS.phoneHref}
      onClick={() => track(slug, "call")}
      data-testid="local-action-call"
      className="inline-flex items-center gap-2 rounded-md border border-pg-border bg-white px-5 py-3 text-sm font-semibold text-pg-dark hover:border-pg-primary transition-colors"
    >
      <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
    </a>
    <a
      href={BUSINESS.whatsapp}
      target="_blank"
      rel="noreferrer"
      onClick={() => track(slug, "whatsapp")}
      data-testid="local-action-whatsapp"
      className="inline-flex items-center gap-2 rounded-md border border-pg-border bg-white px-5 py-3 text-sm font-semibold text-pg-dark hover:border-pg-primary transition-colors"
    >
      <MessageCircle className="h-4 w-4" /> WhatsApp
    </a>
  </div>
);

/** Mobile-only sticky action bar. Hidden on desktop; sits above the safe area. */

/** The four local actions, used in page content. The sticky phone bar lives in MobileActionBar. */
const PROPERTY_TYPES = ["House", "Flat or apartment", "Bungalow", "Period or listed property",
                        "HMO or shared house", "Commercial property", "Other"];
const CUSTOMER_TYPES = ["Individual customer", "Business customer", "Other"];

export const LocalEnquiryForm = ({ location, county }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", postcode: "", property_type: "", customer_type: "",
    message: "", preferred_contact: "email", consent: false,
  });
  const [state, setState] = useState({ sending: false, done: null, error: "" });
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const areaCheck = useServiceArea(form.postcode, "enquiry");

  const submit = async (event) => {
    event.preventDefault();
    setState({ sending: true, done: null, error: "" });
    try {
      const { data } = await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        postcode: form.postcode,
        service: "Damp and mould investigation",
        message: form.message,
        preferred_contact: form.preferred_contact,
        marketing_consent: false,
        property_type: form.property_type,
        customer_type: form.customer_type,
        location_slug: location.slug,
        location_town: location.name,
        location_county: county,
        landing_page: window.location.pathname,
      });
      track(location.slug, "enquiry");
      setState({ sending: false, done: data, error: "" });
    } catch (error) {
      setState({ sending: false, done: null, error: apiError(error) });
    }
  };

  if (state.done)
    return (
      <Notice testId="local-enquiry-success">
        Thank you — your enquiry reference is <strong>{state.done.reference}</strong>. {state.done.message}
      </Notice>
    );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="local-enquiry-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Your name" required>
          <TextInput value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="local-name" />
        </Field>
        <Field label="Email address" required>
          <TextInput type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="local-email" />
        </Field>
        <Field label="Telephone">
          <TextInput value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="local-phone" />
        </Field>
        <Field label="Property postcode" hint={`We will confirm the property is within our ${county} coverage.`}>
          <TextInput value={form.postcode} onChange={(e) => set("postcode", e.target.value)} data-testid="local-postcode" />
        </Field>
        <Field label="Property type">
          <SelectInput
            options={PROPERTY_TYPES.map((t) => ({ value: t, label: t }))}
            value={form.property_type}
            onChange={(e) => set("property_type", e.target.value)}
            placeholder="Select"
            data-testid="local-property-type"
          />
        </Field>
        <Field label="You are">
          <SelectInput
            options={CUSTOMER_TYPES.map((t) => ({ value: t, label: t }))}
            value={form.customer_type}
            onChange={(e) => set("customer_type", e.target.value)}
            placeholder="Select"
            data-testid="local-customer-type"
          />
        </Field>
      </div>
      <Field label="What are you seeing?" required hint="Where the problem is, when it appears and anything that has already been done.">
        <TextArea value={form.message} onChange={(e) => set("message", e.target.value)} required data-testid="local-message" />
      </Field>
      <label className="flex gap-3 text-[13px] leading-relaxed text-pg-muted sm:text-xs">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          required
          data-testid="local-consent"
          className="mt-0.5 h-5 w-5 shrink-0 accent-[#1468E8] sm:h-4 sm:w-4"
        />
        <span>
          I agree to Procure Group Ltd contacting me about this enquiry. Photographs and reports you
          send are used only to assess your enquiry and are held securely with access limited to
          authorised staff.
        </span>
      </label>
      {state.error && <Notice tone="error" testId="local-enquiry-error">{state.error}</Notice>}
      <ServiceAreaNotice area={areaCheck.area} checking={areaCheck.checking} />
      <button
        type="submit"
        disabled={state.sending || areaCheck.refused || areaCheck.checking}
        data-testid="local-enquiry-submit"
        className="rounded-md bg-pg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-pg-dark disabled:opacity-50 transition-colors"
      >
        {state.sending ? "Sending…" : "Request a callback"}
      </button>
    </form>
  );
};

export const locationFaqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqs || []).map((pair) => ({
    "@type": "Question",
    name: Array.isArray(pair) ? pair[0] : pair.question,
    acceptedAnswer: { "@type": "Answer", text: Array.isArray(pair) ? pair[1] : pair.answer },
  })),
});

export const localBusinessJsonLd = (place, districts, path, extra = {}) => ({
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  "@id": `https://procuregroup.uk${path}#business`,
  address: {
    "@type": "PostalAddress",
    streetAddress: "Bellingham House, 2 Huntington Road",
    addressLocality: "St Neots",
    addressRegion: "Cambridgeshire",
    postalCode: "PE19 1BG",
    addressCountry: "GB",
  },
  priceRange: "££",
  name: `Procure Group Ltd — ${place}`,
  parentOrganization: { "@id": "https://procuregroup.uk/#organisation" },
  url: `https://procuregroup.uk${path}`,
  telephone: "+44 333 335 6152",
  email: "info@procuregroup.uk",
  ...(extra.geo ? {
    geo: { "@type": "GeoCoordinates", latitude: extra.geo.lat, longitude: extra.geo.lng },
    hasMap: `https://www.google.com/maps/search/?api=1&query=${extra.geo.lat},${extra.geo.lng}`,
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: { "@type": "GeoCoordinates", latitude: extra.geo.lat, longitude: extra.geo.lng },
      geoRadius: "16000",
    },
  } : {}),
  ...(extra.image ? { image: `https://procuregroup.uk${extra.image}` } : {}),
  areaServed: [
    { "@type": "City", name: place },
    ...(districts || []).map((district) => ({
      "@type": "DefinedRegion",
      addressCountry: "GB",
      postalCode: district,
    })),
  ],
  ...(extra.services?.length ? {
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `Damp, mould and property preservation in ${place}`,
      itemListElement: extra.services.slice(0, 10).map((service) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: `${service.title} in ${place}`,
          serviceType: service.category || "Damp, mould and property preservation",
          provider: { "@id": "https://procuregroup.uk/#organisation" },
          areaServed: { "@type": "City", name: place },
        },
      })),
    },
  } : {}),
  makesOffer: {
    "@type": "Offer",
    itemOffered: {
      "@type": "Service",
      name: `Damp and mould investigation in ${place}`,
      serviceType: "Damp, mould and property preservation",
      provider: { "@id": "https://procuregroup.uk/#organisation" },
      areaServed: { "@type": "City", name: place },
    },
  },
  mainEntityOfPage: `https://procuregroup.uk${path}`,
});

/** The page itself, so its review date and lead photograph are stated rather than implied. */
export const localPageJsonLd = (path, { name, description, reviewed, image }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `https://procuregroup.uk${path}#webpage`,
  url: `https://procuregroup.uk${path}`,
  name,
  description,
  isPartOf: { "@id": "https://procuregroup.uk/#website" },
  about: { "@id": `https://procuregroup.uk${path}#business` },
  ...(reviewed ? { dateModified: reviewed, lastReviewed: reviewed } : {}),
  ...(image ? {
    primaryImageOfPage: { "@type": "ImageObject", url: `https://procuregroup.uk${image}` },
  } : {}),
  inLanguage: "en-GB",
});

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.label,
    item: `https://procuregroup.uk${item.path}`,
  })),
});
